/**
 * AdminLayout - Sidebar-Navigation für Admin-Bereich
 * 
 * PR-NAV: Navigation aus config/navigation.ts (Single Source of Truth)
 * 
 * Responsive Layout:
 * - Desktop (≥1024px): Sidebar-Navigation mit Gruppen
 * - Mobile (<1024px): Header + Bottom-Navigation + "Mehr"
 */

import { ReactNode, useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, ChevronDown, ChevronRight } from 'lucide-react';
import { useAuth } from '../App';
import { UserMenu } from './UserMenu';
import { TwoFactorGraceBanner } from './TwoFactorGraceBanner';
import { ModuleBadge } from './ModuleBadge';

import { useModules } from '../hooks/useModules';
import { AppFooter } from './AppFooter';
import { hasUnseenChangelog } from '../utils/changelog';
import {
  NAV_GROUPS,
  NAV_ITEMS,
  MOBILE_NAV_ITEMS,
  ICON_MAP,
  filterNavItems,
  filterNavGroups,
  groupNavItems,
  type UserRole,
  type NavCategory,
} from '../config/navigation';

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, refresh } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { isEnabled, getTooltip } = useModules();

  // RBAC: Rollen-Check
  const userRole = (user?.role ?? 'WORKER') as UserRole;
  const isAdmin = user && ['ADMIN', 'SUPERADMIN', 'SYSTEM'].includes(user.role);
  const isSystem = user?.role === 'SYSTEM';


  const isActivePath = (href: string) => {
    if (href === '/admin') {
      return location.pathname === '/admin' || location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(href);
  };

  // Navigation filtern basierend auf Rolle + Module (aus navigation.ts)
  const filteredItems = filterNavItems(NAV_ITEMS, userRole, isEnabled);
  const groupedItems = groupNavItems(filteredItems);
  const filteredGroups = filterNavGroups(NAV_GROUPS, userRole);
  
  // Dashboard + Neuigkeiten (Top-Level, keine Gruppe)
  const dashboardItem = filteredItems.find(item => item.id === 'dashboard');
  const changelogItem = filteredItems.find(item => item.id === 'changelog');
  // Bei jeder Navigation neu ausgewertet (Re-Render via useLocation)
  const changelogUnseen = hasUnseenChangelog();
  
  // Mobile Nav filtern
  const filteredMobileItems = filterNavItems(MOBILE_NAV_ITEMS, userRole, isEnabled);
  
  // Collapsible Groups State - persistiert in localStorage
  const SIDEBAR_STATE_KEY = 'wash-ops-sidebar-state';
  
  const getActiveGroup = (): NavCategory | null => {
    for (const item of filteredItems) {
      if (isActivePath(item.href) && item.category !== 'dashboard') {
        return item.category;
      }
    }
    return null;
  };
  
  // Lade persistierten State aus localStorage oder initialisiere Default
  const getInitialExpandedGroups = (): Set<NavCategory> => {
    // Versuche aus localStorage zu laden
    try {
      const stored = localStorage.getItem(SIDEBAR_STATE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        const expanded = new Set<NavCategory>(parsed as NavCategory[]);
        
        // Aktive Gruppe immer öffnen (auch wenn sie im localStorage geschlossen war)
        const activeGroup = getActiveGroup();
        if (activeGroup) {
          expanded.add(activeGroup);
        }
        
        return expanded;
      }
    } catch {
      // Fallback zu Default bei Fehler
    }
    
    // Default: Alle Gruppen offen außer defaultCollapsed
    const expanded = new Set<NavCategory>();
    for (const group of filteredGroups) {
      const items = groupedItems.get(group.id) || [];
      if (items.length > 0 && !group.defaultCollapsed) {
        expanded.add(group.id);
      }
    }
    
    // Aktive Gruppe immer öffnen
    const activeGroup = getActiveGroup();
    if (activeGroup) {
      expanded.add(activeGroup);
    }
    
    return expanded;
  };
  
  const [expandedGroups, setExpandedGroups] = useState<Set<NavCategory>>(() => getInitialExpandedGroups());
  
  // Persistiere Änderungen in localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STATE_KEY, JSON.stringify([...expandedGroups]));
    } catch {
      // Ignore localStorage errors
    }
  }, [expandedGroups]);
  
  // Automatisch aktive Gruppe öffnen bei Pfad-Änderung
  useEffect(() => {
    const activeGroup = getActiveGroup();
    if (activeGroup && !expandedGroups.has(activeGroup)) {
      setExpandedGroups(prev => new Set([...prev, activeGroup]));
    }
  }, [location.pathname]);
  
  const toggleGroup = (groupId: NavCategory) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col lg:flex-row">
      {/* Desktop Sidebar - nur ab lg sichtbar */}
      <aside data-testid="desktop-sidebar" className="hidden lg:flex w-64 bg-white shadow-lg flex-col h-screen fixed left-0 top-0">
        {/* Header */}
        <div className="p-4 border-b shrink-0">
          <h1 className="text-xl font-bold text-slate-800">Administration</h1>
        </div>

        {/* Navigation mit einklappbaren Gruppen */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {/* Dashboard - Top-Level, keine Gruppe */}
          {dashboardItem && (() => {
            const Icon = ICON_MAP[dashboardItem.iconKey];
            return (
              <Link
                to={dashboardItem.href}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors mb-4 ${
                  isActivePath(dashboardItem.href)
                    ? 'bg-slate-100 text-slate-900 font-medium'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon className={`w-5 h-5 ${
                  isActivePath(dashboardItem.href) ? 'text-slate-700' : 'text-slate-500'
                }`} />
                <span>{dashboardItem.label}</span>
              </Link>
            );
          })()}

          {/* Neuigkeiten - Top-Level unter Dashboard, mit Hinweispunkt bei
              ungelesener Version (localStorage, siehe utils/changelog.ts) */}
          {changelogItem && (() => {
            const Icon = ICON_MAP[changelogItem.iconKey];
            return (
              <Link
                to={changelogItem.href}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors -mt-3 mb-4 ${
                  isActivePath(changelogItem.href)
                    ? 'bg-slate-100 text-slate-900 font-medium'
                    : 'text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon className={`w-5 h-5 ${
                  isActivePath(changelogItem.href) ? 'text-slate-700' : 'text-slate-500'
                }`} />
                <span className="flex items-center gap-2">
                  {changelogItem.label}
                  {changelogUnseen && (
                    <span className="w-2 h-2 rounded-full bg-red-500" aria-label="neue Einträge" />
                  )}
                </span>
              </Link>
            );
          })()}

          {/* Collapsible Groups */}
          {filteredGroups.map((group) => {
            const items = groupedItems.get(group.id) || [];
            if (items.length === 0) return null;
            
            const isExpanded = expandedGroups.has(group.id);
            const hasActiveItem = items.some(item => isActivePath(item.href));
            
            return (
              <div key={group.id} className="mb-2">
                {/* Group Header - klickbar */}
                <button
                  onClick={() => toggleGroup(group.id)}
                  className={`flex items-center justify-between w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg transition-colors ${
                    hasActiveItem
                      ? 'text-slate-700 bg-slate-50'
                      : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <span>{group.label}</span>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {/* Group Items - nur wenn expanded */}
                {isExpanded && (
                  <div className="mt-1 space-y-1">
                    {items
                      .filter(item => !item.parentId) // Nur Top-Level Items
                      .map((item) => {
                        const Icon = ICON_MAP[item.iconKey];
                        // Finde Child-Items für dieses Parent
                        const children = items.filter(child => child.parentId === item.id);
                        
                        return (
                          <div key={item.id}>
                            <Link
                              to={item.href}
                              className={`flex items-center gap-3 px-3 py-2 pl-6 rounded-lg transition-colors ${
                                isActivePath(item.href)
                                  ? 'bg-slate-100 text-slate-900 font-medium'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <Icon className={`w-5 h-5 ${
                                isActivePath(item.href) ? 'text-slate-700' : 'text-slate-500'
                              }`} />
                              <span className="flex items-center gap-2">
                                {item.label}
                                {item.moduleKey && <ModuleBadge module={item.moduleKey} />}
                              </span>
                            </Link>
                            {/* Child Items (Submenu) */}
                            {children.length > 0 && (
                              <div className="ml-4 mt-1 space-y-1 border-l border-slate-200 pl-2">
                                {children.map((child) => {
                                  const ChildIcon = ICON_MAP[child.iconKey];
                                  return (
                                    <Link
                                      key={child.href}
                                      to={child.href}
                                      className={`flex items-center gap-3 px-3 py-1.5 pl-4 rounded-lg transition-colors text-sm ${
                                        isActivePath(child.href)
                                          ? 'bg-slate-100 text-slate-900 font-medium'
                                          : 'text-slate-600 hover:bg-slate-50'
                                      }`}
                                    >
                                      <ChildIcon className={`w-4 h-4 ${
                                        isActivePath(child.href) ? 'text-slate-600' : 'text-slate-400'
                                      }`} />
                                      <span className="flex items-center gap-2">
                                        {child.label}
                                        {child.moduleKey && <ModuleBadge module={child.moduleKey} />}
                                      </span>
                                    </Link>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

      </aside>

      {/* Mobile Header - nur unter lg sichtbar */}
      <header className="lg:hidden bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-lg font-bold text-slate-800">Administration</h1>
          <UserMenu />
        </div>
      </header>

      {/* Hauptbereich */}
      <main data-testid="main-content" className="flex-1 lg:ml-64 flex flex-col">
        {/* Desktop Top-Bar mit UserMenu */}
        <div className="hidden lg:flex items-center justify-end h-14 px-8 bg-white border-b">
          <UserMenu />
        </div>
        {/* Gap 9: TwoFactorGraceBanner */}
        <TwoFactorGraceBanner />
        <div className="flex-1 p-4 pb-28 lg:p-8 lg:pb-16">
          {children}
        </div>
        <AppFooter position="fixed inset-x-0 bottom-16 z-40 lg:bottom-0 lg:left-64" />
      </main>

      {/* Mobile Bottom Navigation - nur unter lg sichtbar */}
      <nav data-testid="mobile-nav" className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t z-40">
        <div className="flex justify-around items-center h-16">
          {filteredMobileItems
            .slice(0, 4)
            .map((item) => {
              const Icon = ICON_MAP[item.iconKey];
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex flex-col items-center justify-center min-w-[64px] py-1 ${
                    isActivePath(item.href)
                      ? 'text-blue-600'
                      : 'text-slate-600 hover:text-blue-600'
                  }`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-xs mt-1">{item.label.split('-')[0]}</span>
                </Link>
              );
            })}
          {/* "Mehr"-Link entfernt: die Route /admin/more existiert nicht und
              landete über den Catch-All auf dem Dashboard (Review NW16) */}
        </div>
      </nav>
    </div>
  );
}
